import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from "react";
import { Check, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { MessageResponse } from "@/components/ai-elements/message";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ApiError } from "@/lib/api";
import {
  coerceProposalStatus,
  createHermesApi,
  type HermesProposalDecision,
} from "@/lib/hermes-api";
import {
  HERMES_WRITE_ACCEPTED_EVENT,
  type HermesWriteAcceptedDetail,
} from "@/lib/hermes-knowledge-entry";
import {
  brandIdFromPayload,
  brandSiteRollbackSummary,
  classifyWriteConfirmKind,
  isKbPageCreate,
  journeyIdFromPayload,
  journeyNodesFromPayload,
  journeyNodesPreviewMarkdown,
  kbPagePathFromPayload,
  kbPageTitleFromPayload,
  resolveAfterMarkdown,
  resolveBeforeMarkdown,
  splitMarkdownFrontmatter,
  structuredFieldEntries,
  truncateJourneyBody,
  writeConfirmCardTitle,
  writeConfirmSupportsPreview,
  writeConfirmTone,
  writeConfirmToneCardClass,
  type WriteConfirmKind,
} from "@/lib/hermes-write-confirm";
import { getBrandSiteRelease, type BrandSiteReleasePage } from "@/lib/brand-site-api";
import { BrandSiteReleasePreviewDialog } from "@/components/brand-site/BrandSiteReleasePreviewDialog";
import { ConfirmPreviewDialog } from "./ConfirmPreviewDialog";

type WriteConfirmCardProps = {
  accountId: string;
  proposalId: number;
  action?: string;
  payload?: Record<string, unknown> | null;
  /** Known status from message payload; still revalidated from API on mount. */
  initialStatus?: HermesProposalDecision;
  onResolved?: (status: "accepted" | "rejected") => void;
};

type PreviewMode = "after" | "diff";

function dispatchAccepted(action: string | undefined, payload: Record<string, unknown> | null | undefined) {
  const act = action ?? "unknown";
  const detail: HermesWriteAcceptedDetail = {
    action: act,
    payload: payload ?? null,
  };
  if (act === "journey_upsert") {
    detail.journey_id = journeyIdFromPayload(payload);
    detail.brand_id = brandIdFromPayload(payload);
  }
  window.dispatchEvent(new CustomEvent(HERMES_WRITE_ACCEPTED_EVENT, { detail }));
}

function FrontmatterTable({
  rows,
}: {
  rows: ReturnType<typeof splitMarkdownFrontmatter>["frontmatterRows"];
}) {
  if (rows.length === 0) return null;
  return (
    <div className="mb-4 overflow-x-auto rounded-lg border border-border/70 bg-background/90">
      <table className="w-full min-w-[16rem] border-collapse text-left text-xs">
        <caption className="sr-only">文档元数据（frontmatter）</caption>
        <thead>
          <tr className="border-b border-border/70 bg-muted/50">
            <th scope="col" className="px-2.5 py-1.5 font-medium text-muted-foreground">
              字段
            </th>
            <th scope="col" className="px-2.5 py-1.5 font-medium text-muted-foreground">
              值
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-border/40 last:border-b-0">
              <th
                scope="row"
                className="whitespace-nowrap px-2.5 py-1.5 align-top font-mono text-[11px] font-normal text-muted-foreground"
              >
                {row.key}
              </th>
              <td className="px-2.5 py-1.5 align-top break-all text-foreground">{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MarkdownPane({ markdown, emptyHint }: { markdown: string; emptyHint: string }) {
  if (!markdown.trim()) {
    return <p className="text-sm text-muted-foreground">{emptyHint}</p>;
  }
  const { frontmatterRows, body } = splitMarkdownFrontmatter(markdown);
  const bodyText = body.trim();
  return (
    <div className="size-full">
      <FrontmatterTable rows={frontmatterRows} />
      {bodyText ? (
        <MessageResponse className="size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          {bodyText}
        </MessageResponse>
      ) : frontmatterRows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyHint}</p>
      ) : null}
    </div>
  );
}

function SeedFieldsList({
  fields,
}: {
  fields: ReturnType<typeof structuredFieldEntries>;
}) {
  if (fields.length === 0) {
    return <p className="text-sm text-muted-foreground">暂无字段内容</p>;
  }
  return (
    <div className="space-y-2">
      {fields.map((row) => (
        <div
          key={row.key}
          className="rounded-md border border-border/70 bg-background/80 px-2.5 py-2"
        >
          <p className="text-xs font-medium text-muted-foreground">{row.label}</p>
          <p className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">
            {row.value || "（空）"}
          </p>
        </div>
      ))}
    </div>
  );
}

function CardSummary({
  kind,
  payload,
  action,
}: {
  kind: WriteConfirmKind;
  payload: Record<string, unknown> | null | undefined;
  action?: string;
}) {
  if (kind === "kb_page") {
    const title = kbPageTitleFromPayload(payload);
    const path = kbPagePathFromPayload(payload);
    const create = isKbPageCreate(payload);
    return (
      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
        {title ? <p className="text-sm font-medium text-foreground">{title}</p> : null}
        {path ? <p className="truncate font-mono text-[11px]">{path}</p> : null}
        <p>{create ? "新建页面" : "覆盖已有页面"}</p>
        <p className="text-[11px] text-muted-foreground/80">点击卡片预览完整内容</p>
      </div>
    );
  }

  if (kind === "seed_fields") {
    const fields = structuredFieldEntries(payload);
    return (
      <div className="mt-2 max-h-40 space-y-1.5 overflow-auto">
        {fields.slice(0, 4).map((row) => (
          <div key={row.key} className="rounded-md border border-border/60 bg-background/70 px-2 py-1.5">
            <p className="text-[11px] text-muted-foreground">{row.label}</p>
            <p className="truncate text-xs text-foreground">{row.value || "（空）"}</p>
          </div>
        ))}
        {fields.length > 4 ? (
          <p className="text-[11px] text-muted-foreground">另有 {fields.length - 4} 项，点开预览查看全部</p>
        ) : (
          <p className="text-[11px] text-muted-foreground/80">点击卡片查看全部字段</p>
        )}
      </div>
    );
  }

  if (kind === "kb_delete") {
    const path = kbPagePathFromPayload(payload);
    const slug = typeof payload?.slug === "string" ? payload.slug : null;
    return (
      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
        <p className="font-medium text-destructive">删除后不可从确认卡恢复</p>
        {path ? (
          <p className="truncate font-mono text-[11px] text-foreground/80">{path}</p>
        ) : slug ? (
          <p className="truncate font-mono text-[11px] text-foreground/80">{slug}</p>
        ) : null}
        <p className="text-[11px] text-muted-foreground/80">点击卡片查看详情</p>
      </div>
    );
  }

  if (kind === "journey_upsert") {
    const journeyName = typeof payload?.name === "string" ? payload.name.trim() : "";
    const nodes = journeyNodesFromPayload(payload);
    const isUpdate = journeyIdFromPayload(payload) != null;
    return (
      <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
        {journeyName ? (
          <p className="text-sm font-medium text-foreground">{journeyName}</p>
        ) : null}
        <p>
          {isUpdate ? "更新" : "新建"}旅程手册 · {nodes.length} 个节点
        </p>
        <div className="max-h-28 space-y-1 overflow-auto">
          {nodes.slice(0, 3).map((node, index) => (
            <p key={`${node.offset_days}-${index}`} className="truncate text-foreground/90">
              <span className="font-medium text-foreground">D+{node.offset_days}</span>
              {" · "}
              {truncateJourneyBody(node.summary) || "（空）"}
            </p>
          ))}
          {nodes.length > 3 ? (
            <p className="text-[11px] text-muted-foreground">另有 {nodes.length - 3} 步，点开预览</p>
          ) : (
            <p className="text-[11px] text-muted-foreground/80">点击卡片预览全部节点提示词</p>
          )}
        </div>
      </div>
    );
  }

  if (kind === "brand_site_release_rollback") {
    const summary = brandSiteRollbackSummary(payload);
    return (
      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
        <p className="text-sm font-medium text-foreground">{summary.text}</p>
        <p>只改正式版指针，不复制页面正文</p>
        <p className="text-[11px] text-muted-foreground/80">点击卡片对照当前版与目标版</p>
      </div>
    );
  }

  if (kind === "generic" && payload) {
    return (
      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
        {action ? <p>操作：{action}</p> : null}
        <p className="text-[11px] text-muted-foreground/80">点击卡片查看完整内容</p>
      </div>
    );
  }

  return action && kind !== "create_brand" ? (
    <p className="mt-1 text-xs text-muted-foreground">操作：{action}</p>
  ) : null;
}

export function WriteConfirmCard({
  accountId,
  proposalId,
  action,
  payload,
  initialStatus,
  onResolved,
}: WriteConfirmCardProps) {
  const [status, setStatus] = useState<HermesProposalDecision | "loading">(
    initialStatus ?? "loading",
  );
  const [busy, setBusy] = useState(false);
  const [brandName, setBrandName] = useState(
    typeof payload?.name === "string" ? payload.name : "",
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<PreviewMode>("after");
  const [beforeText, setBeforeText] = useState<string | null>(null);
  const [afterText, setAfterText] = useState<string | null>(null);
  const [rollbackSide, setRollbackSide] = useState<"current" | "target">("target");
  const [rollbackPages, setRollbackPages] = useState<BrandSiteReleasePage[] | null>(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackError, setRollbackError] = useState<string | null>(null);

  const kind = useMemo(() => classifyWriteConfirmKind(action, payload), [action, payload]);
  const tone = useMemo(() => writeConfirmTone(kind, payload), [kind, payload]);
  const fieldRows = useMemo(() => structuredFieldEntries(payload), [payload]);
  const isCreateBrand = kind === "create_brand";
  const rationale = typeof payload?.rationale === "string" ? payload.rationale : null;
  const isPending = status === "pending";
  const isDecided = status === "accepted" || status === "rejected";
  const acceptChosen = status === "accepted";
  const rejectChosen = status === "rejected";
  const actionsDisabled = !isPending || busy;
  const supportsPreview = writeConfirmSupportsPreview(kind);
  const acceptLabel =
    tone === "delete"
      ? "确认删除"
      : isCreateBrand
        ? "确认创建"
        : kind === "brand_site_release_rollback"
          ? "确认回滚"
          : "确认写入";
  const rollbackSummary = useMemo(
    () => brandSiteRollbackSummary(payload),
    [payload],
  );
  const brandSiteId = useMemo(() => {
    const n = Number(payload?.brand_site_id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [payload?.brand_site_id]);
  const fromReleaseId = useMemo(() => {
    const n = Number(payload?.from_release_id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [payload?.from_release_id]);
  const toReleaseId = useMemo(() => {
    const n = Number(payload?.to_release_id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [payload?.to_release_id]);
  const activeRollbackReleaseId =
    rollbackSide === "current" ? fromReleaseId : toReleaseId;

  const afterMarkdown = useMemo(
    () => resolveAfterMarkdown(payload, afterText),
    [payload, afterText],
  );
  const beforeMarkdown = useMemo(
    () => resolveBeforeMarkdown(payload, beforeText),
    [payload, beforeText],
  );
  const journeyPreviewMd = useMemo(
    () => (kind === "journey_upsert" ? journeyNodesPreviewMarkdown(payload) : ""),
    [kind, payload],
  );
  const hasDiff = Boolean(beforeMarkdown.trim() && afterMarkdown.trim());

  useEffect(() => {
    if (typeof payload?.name === "string") setBrandName(payload.name);
  }, [payload?.name]);

  useEffect(() => {
    if (!previewOpen || kind !== "brand_site_release_rollback") return;
    if (!brandSiteId || activeRollbackReleaseId == null) {
      setRollbackPages(null);
      setRollbackError("缺少正式版信息，无法预览");
      return;
    }

    let cancelled = false;
    setRollbackLoading(true);
    setRollbackError(null);
    void getBrandSiteRelease(accountId, brandSiteId, activeRollbackReleaseId)
      .then((payload) => {
        if (cancelled) return;
        setRollbackPages(payload.pages ?? []);
      })
      .catch((error) => {
        if (cancelled) return;
        setRollbackPages(null);
        setRollbackError(error instanceof Error ? error.message : "加载预览失败");
      })
      .finally(() => {
        if (!cancelled) setRollbackLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [previewOpen, kind, accountId, brandSiteId, activeRollbackReleaseId]);

  useEffect(() => {
    if (initialStatus === "accepted" || initialStatus === "rejected") {
      setStatus(initialStatus);
    }

    let cancelled = false;
    async function loadProposal() {
      try {
        const hermes = createHermesApi(accountId);
        const { proposal } = await hermes.getProposal(proposalId);
        if (cancelled) return;
        if (initialStatus !== "accepted" && initialStatus !== "rejected") {
          setStatus(coerceProposalStatus(proposal.status) ?? "pending");
        }
        setBeforeText(typeof proposal.before_text === "string" ? proposal.before_text : null);
        setAfterText(typeof proposal.after_text === "string" ? proposal.after_text : null);
      } catch {
        if (!cancelled && initialStatus !== "accepted" && initialStatus !== "rejected") {
          setStatus("pending");
        }
      }
    }

    void loadProposal();
    return () => {
      cancelled = true;
    };
  }, [accountId, proposalId, initialStatus]);

  async function handleAccept() {
    if (!isPending || busy) return;

    if (isCreateBrand) {
      const trimmed = brandName.trim();
      if (!trimmed) {
        setNameError("请输入品牌名称");
        return;
      }
      if (trimmed.length > 100) {
        setNameError("品牌名称不能超过 100 个字符");
        return;
      }
      setNameError(null);
    }

    setBusy(true);
    try {
      const hermes = createHermesApi(accountId);
      const { proposal } = await hermes.acceptProposal(
        proposalId,
        isCreateBrand ? { name: brandName.trim() } : undefined,
      );
      const next = coerceProposalStatus(proposal.status) ?? "accepted";
      setStatus(next === "pending" ? "accepted" : next);
      const responsePayload =
        proposal.payload && typeof proposal.payload === "object" ? proposal.payload : payload;
      dispatchAccepted(
        action ?? proposal.action,
        isCreateBrand ? { ...responsePayload, name: brandName.trim() } : responsePayload,
      );
      onResolved?.("accepted");
      toast.success(
        kind === "journey_upsert"
          ? "已写入旅程手册"
          : kind === "brand_site_release_rollback"
            ? "已回滚正式版"
            : isCreateBrand
              ? "已创建品牌"
              : "已写入知识库",
      );
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "确认失败，请稍后重试";
      toast.error(message);
      if (isCreateBrand) setNameError(message);
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    if (!isPending || busy) return;

    setBusy(true);
    try {
      const hermes = createHermesApi(accountId);
      const { proposal } = await hermes.rejectProposal(proposalId);
      const next = coerceProposalStatus(proposal.status) ?? "rejected";
      setStatus(next === "pending" ? "rejected" : next);
      onResolved?.("rejected");
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "拒绝失败，请稍后重试";
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  function openPreview(event?: MouseEvent) {
    event?.preventDefault();
    if (!supportsPreview) return;
    if (kind === "brand_site_release_rollback") {
      setRollbackSide("target");
      setPreviewOpen(true);
      return;
    }
    setPreviewMode(hasDiff ? previewMode : "after");
    setPreviewOpen(true);
  }

  const previewTitle =
    kind === "kb_page"
      ? kbPageTitleFromPayload(payload) || "页面预览"
      : kind === "seed_fields"
        ? "种子字段预览"
        : kind === "kb_delete"
          ? "删除确认详情"
          : kind === "journey_upsert"
            ? "旅程手册预览"
            : kind === "brand_site_release_rollback"
              ? "正式版回滚对照"
              : "写入详情";

  const copyText =
    kind === "kb_page"
      ? previewMode === "diff" && hasDiff
        ? `--- 写入前 ---\n${beforeMarkdown}\n\n--- 写入后 ---\n${afterMarkdown}`
        : afterMarkdown
      : kind === "seed_fields"
        ? fieldRows.map((row) => `${row.label}：${row.value || "（空）"}`).join("\n")
        : kind === "kb_delete"
          ? kbPagePathFromPayload(payload) ?? ""
          : kind === "journey_upsert"
            ? journeyPreviewMd
            : payload
              ? JSON.stringify(payload, null, 2)
              : "";

  let previewBody: ReactNode = null;
  if (kind === "kb_page") {
    previewBody =
      previewMode === "diff" && hasDiff ? (
        <div className="space-y-4">
          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">写入前</p>
            <div className="rounded-lg border border-border/50 bg-background/80 p-2">
              <MarkdownPane markdown={beforeMarkdown} emptyHint="（无原文）" />
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">写入后</p>
            <div className="rounded-lg border border-border/50 bg-background/80 p-2">
              <MarkdownPane markdown={afterMarkdown} emptyHint="（无正文）" />
            </div>
          </div>
        </div>
      ) : (
        <MarkdownPane markdown={afterMarkdown} emptyHint="暂无页面正文" />
      );
  } else if (kind === "seed_fields") {
    previewBody = <SeedFieldsList fields={fieldRows} />;
  } else if (kind === "journey_upsert") {
    previewBody = (
      <MarkdownPane markdown={journeyPreviewMd} emptyHint="暂无节点内容" />
    );
  } else if (kind === "kb_delete") {
    previewBody = (
      <div className="space-y-2 text-sm">
        <p className="text-destructive">确认后将删除该知识库内容。</p>
        <p className="font-mono text-xs break-all">
          {kbPagePathFromPayload(payload) || action || "（未知路径）"}
        </p>
        {afterMarkdown || beforeMarkdown ? (
          <MarkdownPane
            markdown={beforeMarkdown || afterMarkdown}
            emptyHint="无页面快照"
          />
        ) : null}
      </div>
    );
  } else {
    previewBody = (
      <pre className="whitespace-pre-wrap text-xs text-foreground">
        {payload ? JSON.stringify(payload, null, 2) : "（无详情）"}
      </pre>
    );
  }

  return (
    <>
      <div
        className={cn(
          "w-full rounded-xl border p-3 text-sm shadow-xs",
          writeConfirmToneCardClass(tone, {
            decided: isDecided,
            interactive: supportsPreview && !isDecided,
          }),
        )}
        role={supportsPreview ? "button" : undefined}
        tabIndex={supportsPreview ? 0 : undefined}
        onClick={supportsPreview ? () => openPreview() : undefined}
        onKeyDown={
          supportsPreview
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openPreview();
                }
              }
            : undefined
        }
      >
        <div className="flex items-start justify-between gap-2">
          <p className="font-medium text-foreground">{writeConfirmCardTitle(kind)}</p>
          {supportsPreview ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
              <Eye className="size-3" />
              预览
            </span>
          ) : null}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {isDecided
            ? "该提案已处理，不可再次操作。"
            : "Hermes 已结束回复，请确认或拒绝后才会写入。"}
        </p>

        {isCreateBrand ? (
          <div
            className="mt-3 space-y-2"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <p className="text-xs text-muted-foreground">
              检测到品牌线索。确认后将在当前账户下创建品牌（不会静默创建）。
            </p>
            {rationale ? <p className="text-xs text-muted-foreground">原因：{rationale}</p> : null}
            <div className="space-y-1.5">
              <Label htmlFor={`brand-name-${proposalId}`}>品牌名称</Label>
              <Input
                id={`brand-name-${proposalId}`}
                value={brandName}
                disabled={actionsDisabled || status === "loading"}
                onChange={(event) => {
                  setBrandName(event.target.value);
                  setNameError(null);
                }}
                aria-invalid={Boolean(nameError)}
              />
              {nameError ? (
                <p className="text-xs text-destructive" role="alert">
                  {nameError}
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <CardSummary kind={kind} payload={payload} action={action} />
        )}

        <div
          className="mt-3 flex flex-wrap items-center gap-2"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Button
            type="button"
            size="sm"
            variant={
              isDecided
                ? acceptChosen
                  ? tone === "delete"
                    ? "destructive"
                    : "default"
                  : "outline"
                : tone === "delete"
                  ? "destructive"
                  : "default"
            }
            disabled={actionsDisabled || status === "loading"}
            className={cn(
              rejectChosen && "opacity-50",
              // 设计系统 destructive 默认为浅底；确认删除用实心红更醒目
              tone === "delete" &&
                !isDecided &&
                "bg-destructive text-white hover:bg-destructive/90 focus-visible:border-destructive focus-visible:ring-destructive/30",
            )}
            onClick={() => void handleAccept()}
          >
            <Check className="size-3.5" />
            {acceptLabel}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={rejectChosen ? "default" : "outline"}
            disabled={actionsDisabled || status === "loading"}
            className={cn(acceptChosen && "opacity-50")}
            onClick={() => void handleReject()}
          >
            <X className="size-3.5" />
            拒绝
          </Button>
        </div>
      </div>

      <ConfirmPreviewDialog
        open={previewOpen && kind !== "brand_site_release_rollback"}
        onOpenChange={setPreviewOpen}
        title={previewTitle}
        description={
          kind === "kb_page"
            ? kbPagePathFromPayload(payload) ?? undefined
            : "确认前可核对完整内容"
        }
        copyText={copyText}
        toolbar={
          kind === "kb_page" && hasDiff ? (
            <div className="flex gap-1">
              <Button
                type="button"
                size="sm"
                variant={previewMode === "after" ? "default" : "outline"}
                onClick={() => setPreviewMode("after")}
              >
                写入后
              </Button>
              <Button
                type="button"
                size="sm"
                variant={previewMode === "diff" ? "default" : "outline"}
                onClick={() => setPreviewMode("diff")}
              >
                对照
              </Button>
            </div>
          ) : null
        }
      >
        {previewBody}
      </ConfirmPreviewDialog>

      <BrandSiteReleasePreviewDialog
        open={previewOpen && kind === "brand_site_release_rollback"}
        onOpenChange={setPreviewOpen}
        title={previewTitle}
        description={
          <p>
            {rollbackSummary.text}
            。确认前可切换查看当前正式版与目标版。
          </p>
        }
        pages={rollbackPages}
        loading={rollbackLoading}
        error={rollbackError}
        compareToggle={{
          side: rollbackSide,
          onSideChange: setRollbackSide,
          currentLabel:
            rollbackSummary.fromNumber != null
              ? `第 ${rollbackSummary.fromNumber} 版`
              : "当前",
          targetLabel:
            rollbackSummary.toNumber != null
              ? `第 ${rollbackSummary.toNumber} 版`
              : "目标",
        }}
      />
    </>
  );
}
